"use client";

import * as Headless from "@headlessui/react";
import clsx from "clsx";
import { LayoutGroup, motion } from "framer-motion";
import React, { forwardRef, useId } from "react";
import { TouchTarget } from "./button";

export const TabNav = ({ className, ...props }: React.ComponentPropsWithoutRef<"nav">) => (
  <nav {...props} className={clsx(className, "flex items-center")} />
);

export const TabNavSection = ({ className, ...props }: React.ComponentPropsWithoutRef<"div">) => {
  const id = useId();

  return (
    <LayoutGroup id={id}>
      <div
        {...props}
        className={clsx(
          className,
          "flex items-center gap-1 rounded-lg border border-zinc-200 bg-zinc-50 p-1 dark:border-zinc-700 dark:bg-zinc-800/50",
        )}
      />
    </LayoutGroup>
  );
};

export const TabNavItem = forwardRef(
  (
    {
      current,
      className,
      children,
      ...props
    }: { current?: boolean; className?: string; children: React.ReactNode } & Omit<Headless.ButtonProps, "as" | "className">,
    ref: React.ForwardedRef<HTMLButtonElement>,
  ) => {
    const classes = clsx(
      // Base
      "relative flex min-w-0 items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
      // Default state (not selected)
      "text-zinc-600 dark:text-zinc-400",
      // Hover (when not current)
      !current && "hover:text-zinc-900 hover:bg-zinc-100 dark:hover:text-white dark:hover:bg-zinc-700/50",
      // Current/selected state
      current && "bg-white text-zinc-900 shadow-sm dark:bg-zinc-700 dark:text-white",
    );

    return (
      <span className={clsx(className, "relative")}>
        {current && (
          <motion.span
            layoutId="tab-indicator"
            className="absolute inset-0 rounded-md bg-white shadow-sm dark:bg-zinc-700"
            transition={{ type: "spring", bounce: 0.15, duration: 0.5 }}
          />
        )}
        <Headless.Button {...props} className={clsx("cursor-default relative z-10", classes)} data-current={current ? "true" : undefined} ref={ref}>
          <TouchTarget>{children}</TouchTarget>
        </Headless.Button>
      </span>
    );
  },
);

TabNavItem.displayName = "TabNavItem";
